import React from "react";

/**
 * Route-level error boundary.
 *
 * Handles two failure modes:
 *
 * 1. `ChunkLoadError` — raised by React.lazy / webpack when a dynamic import
 *    404s. This almost always happens when a user has a stale `index.html`
 *    (cached by their browser or an edge CDN) that references hashed chunk
 *    filenames from a previous deploy. The only reliable recovery is a full
 *    page reload against the fresh HTML.
 *
 *    We auto-reload ONCE per tab (guarded via sessionStorage) so the user
 *    never sees the error screen for transient stale-bundle cases. If the
 *    reload also fails (the real chunk is actually broken), we fall through
 *    to the manual fallback screen so we don't reload-loop.
 *
 * 2. Any other uncaught render error — shows the generic fallback UI.
 */
const RELOAD_GUARD_KEY = "app.chunkReload.guard";

function isChunkLoadError(error) {
  if (!error) return false;
  if (error.name === "ChunkLoadError") return true;
  const msg = String(error.message || "");
  return (
    msg.includes("Loading chunk") ||
    msg.includes("Loading CSS chunk") ||
    // Webpack occasionally surfaces this when index.html is served as JS
    (error.name === "SyntaxError" && msg.includes("Unexpected token"))
  );
}

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, isChunkError: false };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error,
      isChunkError: isChunkLoadError(error),
    };
  }

  componentDidCatch(error, errorInfo) {
    const chunkError = isChunkLoadError(error);
    console.error("[ErrorBoundary] Caught error:", error, errorInfo);

    if (chunkError) {
      // Only auto-reload once per tab to avoid infinite reload loops if the
      // new HTML itself references a broken chunk.
      let alreadyReloaded = false;
      try {
        alreadyReloaded = sessionStorage.getItem(RELOAD_GUARD_KEY) === "1";
      } catch {
        // sessionStorage can throw in private-mode iframes — ignore.
      }

      if (!alreadyReloaded) {
        try {
          sessionStorage.setItem(RELOAD_GUARD_KEY, "1");
        } catch {
          // noop
        }
        // Use replace() so the error screen doesn't end up in history.
        window.location.reload();
      }
    } else {
      // Clear the reload guard on any non-chunk error so the next stale-bundle
      // incident gets the auto-reload again.
      try {
        sessionStorage.removeItem(RELOAD_GUARD_KEY);
      } catch {
        // noop
      }
    }
  }

  handleManualReload = () => {
    try {
      sessionStorage.removeItem(RELOAD_GUARD_KEY);
    } catch {
      // noop
    }
    window.location.reload();
  };

  handleTryAgain = () => {
    this.setState({ hasError: false, error: null, isChunkError: false });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      if (this.state.isChunkError) {
        return (
          <div className="flex flex-col items-center justify-center min-h-[200px] p-8 text-center">
            <p className="text-indigo-600 font-medium mb-2">
              A new version was just deployed.
            </p>
            <p className="text-gray-500 text-sm mb-4 max-w-md">
              Please reload the page to pick up the latest files. Your work is not lost.
            </p>
            <button
              type="button"
              onClick={this.handleManualReload}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded text-sm font-medium"
            >
              Reload page
            </button>
          </div>
        );
      }

      return (
        <div className="flex flex-col items-center justify-center min-h-[200px] p-8 text-center">
          <p className="text-red-500 font-medium mb-2">Something went wrong.</p>
          <p className="text-gray-500 text-sm mb-4">
            {this.state.error?.message || "An unexpected error occurred."}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={this.handleTryAgain}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded text-sm"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={this.handleManualReload}
              className="px-4 py-2 bg-gray-800 hover:bg-gray-900 text-white rounded text-sm"
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
